import {isObject} from '@utils/isObject'
import {IApplicationDefinition, IAppRegistry} from '@apps-config/app-definition'
import {RoutesHelper} from '@apps-config/routing/routing'
import {Activity} from '@utils/activity-tracking'
import {isFlagOn} from '@telstra-connect/feature-flags'
import {getDashboardFlags} from '@telstra-connect/dashboard/feature-flags/load-flags'
import {ApplicationName as ServicePerformaceActivityTrackingApplicationName} from '@telstra-connect/service-performance/activity-tracking'
import {
  getServicePerformanceFlags,
  isFlagOn as isServicePerformanceFlagOn,
} from '@telstra-connect/service-performance/feature-flags'
import type {IServicePerformanceAppState} from '@telstra-connect/service-performance/state'
import {performanceReducer as ServicePerformanceReducers} from '@telstra-connect/service-performance/reducers'
import {secureRouteBuilder} from '@applications/secure-route'
import Routes from '@applications/service-performance/route-constants'
import ViewSiteServicesList from '@applications/service-performance/pages/view-site-detail/index-lazy'
import SurfaceContainer from '@components/card-surface-container/surface-container-lazy'
import Map from '@applications/service-performance/components/maps'
import {TMapFactory} from '@applications/apps-config/app-definition'
import ViewServicesWithData from '@applications/service-performance/pages/view-services-with-data/index-lazy'
import ViewServiceWithDataDetails from '@applications/service-performance/pages/view-service-with-data-details/index-lazy'
import {IUserProfile} from '@telstra-connect/login/state'
import {getServicePerformanceFlagName} from '@applications'
import {IAuthorizedRequestState} from '@telstra-connect/utils/http/requests/request'
import {getCompanyIdInit, isInternationalCustomer} from '@telstra-connect/config/config'
import NewviewServicesWithData from './pages/new-view-services-with-data/index-lazy'
import ViewRouteManagementDetails from '@applications/service-performance/pages/view-route-management-details-int/index-lazy'
import AddPrefixHomePage from '@applications/service-performance/pages/add-prefix-int/index-lazy'
import ViewSuccessMessageDetails from './pages/view-success-message-int/index-lazy'
import ViewAddDNSDetailsPage from '@applications/service-performance/pages/add-dns-record/index-lazy'
import ViewDNSManagementDetails from '@applications/service-performance/pages/view-dns-management-details-int/index-lazy'
import ViewSuccessDnsMessageDetails from '@applications/service-performance/pages/view-dns-success-message-int/index-lazy'
import IntYourProductServiceHome from '@applications/your-products-and-services/pages/your-product-service-home-int/index-lazy'
import IntAllServicesPageLazy from '@applications/your-products-and-services/pages/all-services-int/index-lazy'
import GoogleMaps from './components/google-maps'
const routes = new RoutesHelper({
  rootPath: secureRouteBuilder(`${Routes.ServicePerformance}/${Routes.PerformanceStatus}`),
  subpagesPathMask: secureRouteBuilder(Routes.ServicePerformance),
})

export const servicePerformanceRoute = routes.getAppPath()

export const applicationName = ServicePerformaceActivityTrackingApplicationName

const tracker = Activity.createTracker(applicationName)

export function SPMDisplayName() {
  // FIXME: window['applications'] is used as global object need to pass this a param
  // pass this a to MFE's and other subapplications platform
  // eslint-disable-next-line dot-notation,@typescript-eslint/dot-notation
  const servicesConfig = window['applications']
  if (servicesConfig && isObject(servicesConfig[applicationName])) {
    return servicesConfig[applicationName].displayName
  }
  return ''
}

export function isSPMAvailable() {
  // FIXME: window['applications'] is used as global object need to pass this a param
  // pass this a to MFE's and other subapplications platform
  // eslint-disable-next-line dot-notation,@typescript-eslint/dot-notation
  const servicesConfig = window['applications']
  if (servicesConfig == null) {
    return false
  }
  if (isObject(servicesConfig[applicationName])) {
    return true
  }
  return false
}

export const Application: IApplicationDefinition = {
  get applicationName() {
    return applicationName
  },

  get acceptsUserFeedback() {
    return true
  },

  get acceptsErrorReports() {
    return true
  },

  get card() {
    return SurfaceContainer
  },

  get map() {
		if (getServicePerformanceFlags().SCMT11562GoogleMap.flagState)
			return GoogleMaps as TMapFactory
		else
    	return Map as TMapFactory
  },

  get tracker() {
    return tracker
  },

  get routes() {
    return routes
  },

  init(
    registry: IAppRegistry,
    availableToUser: boolean,
    userProfile: IUserProfile,
    authorizedRequestState: IAuthorizedRequestState,
  ) {
    const flags = getServicePerformanceFlags()
    const companyId = getCompanyIdInit(userProfile, authorizedRequestState)
    const isInternational = isInternationalCustomer(companyId)
    // For international users no need to have 'Network performance reports' permission
    if (
      isServicePerformanceFlagOn(flags.application) ||
      (isInternational && getServicePerformanceFlagName('yourServicesInternational'))
    ) {
      if (
        (availableToUser && !isInternational) ||
        (isInternational && getServicePerformanceFlagName('yourServicesInternational'))
      ) {
        registry.add(Application)
      }
    }
  },

  render() {
    const enableLandingPageReDesign = getServicePerformanceFlags().enableLandingPageReDesign
    return [
      {
        path: routes.getPageSubpath(Routes.PerformanceServicesTab()),
        component: enableLandingPageReDesign ? NewviewServicesWithData : ViewServicesWithData,
      },
      {
        path: routes.getPageSubpath(Routes.PerformanceStatus),
        component: enableLandingPageReDesign ? NewviewServicesWithData : ViewServicesWithData,
      },
      {
        path: routes.getPageSubpath(Routes.ServicesDetailsBySiteRoute),
        component: ViewSiteServicesList,
      },
      {
        path: routes.getPageSubpath(Routes.ServiceDetailBySiteID),
        component: ViewServiceWithDataDetails,
      },
      {
        path: routes.getPageSubpath(Routes.ServiceDetail),
        component: ViewServiceWithDataDetails,
      },
      {
        path: routes.getPageSubpath(Routes.ServiceDetailInt),
        component: ViewServiceWithDataDetails,
      },
      {
        path: routes.getPageSubpath(Routes.RouteManagementDetailInt),
        component: ViewRouteManagementDetails,
      },
      {
        path: routes.getPageSubpath(Routes.DNSManagementDetailInt),
        component: ViewDNSManagementDetails,
      },
      {
        path: routes.getPageSubpath(Routes.AddPrefix),
        component: AddPrefixHomePage,
      },
      {
        path: routes.getPageSubpath(Routes.RouteManagementSuccessInt),
        component: ViewSuccessMessageDetails,
      },
      {
        path: routes.getPageSubpath(Routes.AddDnsRecordRoute),
        component: ViewAddDNSDetailsPage,
      },
      {
        path: routes.getPageSubpath(Routes.DnsRequestSuccessInt),
        component: ViewSuccessDnsMessageDetails,
      },
      {
        path: routes.getPageSubpath(Routes.yourProductsServicesInt),
        component: IntYourProductServiceHome,
      },
      {
        path: routes.getPageSubpath(Routes.AllServices),
        component: IntAllServicesPageLazy,
      },
    ]
  },

  menuItem(state, navigate) {
    const SiteMenuFeatureFlag = isFlagOn(getDashboardFlags().sideMenu)

    if (SiteMenuFeatureFlag) {
      return [
        {
          testID: 'ui-menu-performance',
          name: SPMDisplayName(),
          intialPagePath: routes.getInitialPagePath(),
          appPath: routes.getAppPath(),
          notificationsCount: 0,
          onClick: () => {
            Application.tracker.trackMenu(SPMDisplayName())
            return navigate(routes.getInitialPagePath())
          },
        },
      ]
    } else {
      return [
        {
          testID: 'ui-menu-performance',
          name: SPMDisplayName(),
          intialPagePath: routes.getInitialPagePath(),
          appPath: routes.getAppPath(),
          notificationsCount: 0,
          onClick: () => {
            Application.tracker.trackMenu(SPMDisplayName())
          },
        },
      ]
    }
  },
}

export {ServicePerformanceReducers, IServicePerformanceAppState, Routes}
